import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import RefreshIcon from "@mui/icons-material/Refresh";
import { hubApi } from "../services/hubApi";

const statusConfig = {
  answered: { label: "Atendida", color: "#25D366", bg: "rgba(37,211,102,0.12)" },
  ended: { label: "Finalizada", color: "#3B82F6", bg: "rgba(59,130,246,0.12)" },
  ringing: { label: "Tocando", color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  missed: { label: "Perdida", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
  failed: { label: "Falhou", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
  rejected: { label: "Rejeitada", color: "#6B7280", bg: "rgba(107,114,128,0.12)" },
};

const tabs = [
  { label: "Todas", value: "" },
  { label: "Realizadas", value: "outbound" },
  { label: "Recebidas", value: "inbound" },
  { label: "Perdidas", value: "missed" },
];

function formatDuration(seconds) {
  if (!seconds) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CallHistory() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [audioPlaying, setAudioPlaying] = useState(null);

  const direction = tabs[tab].value;
  const status = direction === "missed" ? "missed" : undefined;
  const actualDirection =
    direction === "missed" ? undefined : direction || undefined;

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 15,
        ...(actualDirection && { direction: actualDirection }),
        ...(status && { status }),
        ...(search && { search }),
      };
      const { data } = await hubApi.voipGetCallHistory(params);
      setCalls(data.calls || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error loading call history:", err);
    } finally {
      setLoading(false);
    }
  }, [page, actualDirection, status, search]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  const handlePlayRecording = (callId) => {
    if (audioPlaying === callId) {
      setAudioPlaying(null);
      return;
    }
    setAudioPlaying(callId);
  };

  const cellSx = {
    color: "#D1D5DB",
    fontSize: "0.8rem",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    py: 1.5,
  };
  const headSx = {
    ...cellSx,
    color: "#6B7280",
    fontWeight: 600,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6" }}>
          Historico de Chamadas
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Buscar numero..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#1E293B",
                borderRadius: "10px",
                fontSize: "0.8rem",
                "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.15)" },
              },
            }}
          />
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={loadCalls} sx={{ color: "#6B7280" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 36,
          "& .MuiTab-root": {
            color: "#6B7280",
            minHeight: 36,
            fontSize: "0.8rem",
            textTransform: "none",
            fontWeight: 500,
            "&.Mui-selected": { color: "#8B5CF6" },
          },
          "& .MuiTabs-indicator": { bgcolor: "#8B5CF6", height: 2 },
        }}
      >
        {tabs.map((t) => (
          <Tab key={t.value} label={t.label} />
        ))}
      </Tabs>

      {/* Table */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#8B5CF6" }} />
        </Box>
      ) : calls.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#6B7280", fontSize: "0.85rem" }}>
            Nenhuma chamada encontrada
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer
            sx={{
              bgcolor: "#1E293B",
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Direcao</TableCell>
                  <TableCell sx={headSx}>De</TableCell>
                  <TableCell sx={headSx}>Para</TableCell>
                  <TableCell sx={headSx}>Status</TableCell>
                  <TableCell sx={headSx}>Duracao</TableCell>
                  <TableCell sx={headSx}>Data</TableCell>
                  <TableCell sx={headSx} align="center">
                    Gravacao
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((call) => {
                  const st = statusConfig[call.status] || statusConfig.ended;
                  return (
                    <TableRow
                      key={call.id}
                      hover
                      sx={{
                        "&:hover": { bgcolor: "rgba(255,255,255,0.02)" },
                      }}
                    >
                      <TableCell sx={cellSx}>
                        {call.direction === "outbound" ? (
                          <PhoneForwardedIcon
                            sx={{ fontSize: 16, color: "#25D366" }}
                          />
                        ) : (
                          <PhoneCallbackIcon
                            sx={{ fontSize: 16, color: "#3B82F6" }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>{call.from || "-"}</TableCell>
                      <TableCell sx={cellSx}>{call.to || "-"}</TableCell>
                      <TableCell sx={cellSx}>
                        <Chip
                          label={st.label}
                          size="small"
                          sx={{
                            bgcolor: st.bg,
                            color: st.color,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {formatDuration(call.duration)}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {formatDate(call.startedAt)}
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        {call.recordingUrl ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              justifyContent: "center",
                            }}
                          >
                            <Tooltip title="Reproduzir">
                              <IconButton
                                size="small"
                                onClick={() => handlePlayRecording(call.id)}
                                sx={{
                                  color:
                                    audioPlaying === call.id
                                      ? "#8B5CF6"
                                      : "#6B7280",
                                }}
                              >
                                <PlayArrowIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                component="a"
                                href={hubApi.voipGetRecording(call.id)}
                                target="_blank"
                                rel="noopener"
                                sx={{ color: "#6B7280" }}
                              >
                                <DownloadIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography
                            sx={{ color: "#4B5563", fontSize: "0.7rem" }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Audio player inline */}
          {audioPlaying && (
            <Box sx={{ mt: 2, px: 1 }}>
              <audio
                controls
                autoPlay
                src={hubApi.voipGetRecording(audioPlaying)}
                onEnded={() => setAudioPlaying(null)}
                style={{ width: "100%", height: 36 }}
              />
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#6B7280",
                    "&.Mui-selected": {
                      bgcolor: "rgba(139,92,246,0.2)",
                      color: "#8B5CF6",
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
